import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Drawer,
  Form,
  Input,
  Row,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined, SaveOutlined, SendOutlined } from '@ant-design/icons';
import { API_ENDPOINTS } from '../../api/config';
import { get, post, put } from '../../api/request';
import '../DataReporting/DataReportingPage.css';

const { Paragraph, Text } = Typography;

type ReportAction = 'profile' | 'works' | 'asset' | 'recharge' | 'wallet';

type ContentStudioConfig = {
  enabled: boolean;
  platform_code: string;
  base_url: string;
  recharge_url: string;
  sync_secret: string;
  last_sync_status?: string;
};

type ReportTask = {
  id: number;
  scope: 'all' | 'user';
  user_id?: number;
  actions: ReportAction[];
  status: 'pending' | 'running' | 'completed' | 'partial' | 'failed';
  message: string;
  stats: { total: number; success: number; failed: number; skipped: number };
  created_at: string;
  finished_at?: string;
};

type ReportRecord = {
  id: number;
  task_id: number;
  action: string;
  action_label: string;
  status: 'success' | 'failed' | 'skipped';
  method: string;
  endpoint: string;
  request_id: string;
  user_id?: number;
  account_id?: number;
  entity_id?: string;
  payload: Record<string, unknown>;
  response: Record<string, unknown>;
  http_status?: number;
  message: string;
  created_at: string;
};

type Overview = {
  config: { enabled: boolean; base_url: string; platform_key: string };
  users: { total: number; bound: number; unbound: number };
  projects: number;
  orders: number;
  wallet_records: number;
  reports: { success: number; failed: number; skipped: number };
  latest_task?: ReportTask;
};

type ListResponse<T> = { data: T[]; total: number };

const actionOptions = [
  { label: '用户资料', value: 'profile' },
  { label: '作品', value: 'works' },
  { label: '资产快照', value: 'asset' },
  { label: '充值订单', value: 'recharge' },
  { label: '钱包流水', value: 'wallet' },
];

const taskStatus = {
  pending: { color: 'default', label: '等待中' },
  running: { color: 'processing', label: '上报中' },
  completed: { color: 'success', label: '已完成' },
  partial: { color: 'warning', label: '部分失败' },
  failed: { color: 'error', label: '失败' },
};

const reportStatus = {
  success: { color: 'success', label: '成功' },
  failed: { color: 'error', label: '失败' },
  skipped: { color: 'default', label: '跳过' },
};

const dateText = (value?: string) => (value ? new Date(value).toLocaleString() : '-');
const pretty = (value: unknown) => JSON.stringify(value || {}, null, 2);

const DataReportingPage: React.FC = () => {
  const [form] = Form.useForm<ContentStudioConfig>();
  const [overview, setOverview] = useState<Overview>();
  const [tasks, setTasks] = useState<ReportTask[]>([]);
  const [records, setRecords] = useState<ReportRecord[]>([]);
  const [actions, setActions] = useState<ReportAction[]>(['profile', 'works', 'asset', 'recharge', 'wallet']);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [detail, setDetail] = useState<ReportRecord>();

  const loadData = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const [overviewData, taskData, recordData, configData] = await Promise.all([
        get<Overview>(API_ENDPOINTS.lumingReporting.overview),
        get<ListResponse<ReportTask>>(API_ENDPOINTS.lumingReporting.tasks),
        get<ListResponse<ReportRecord>>(API_ENDPOINTS.lumingReporting.records),
        get<ContentStudioConfig>(API_ENDPOINTS.contentStudio.config),
      ]);
      setOverview(overviewData);
      setTasks(taskData.data || []);
      setRecords(recordData.data || []);
      form.setFieldsValue(configData);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载鹿鸣数据上报信息失败');
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const hasActiveTask = tasks.some((task) => task.status === 'pending' || task.status === 'running');
  useEffect(() => {
    if (!hasActiveTask) return undefined;
    const timer = window.setInterval(() => loadData(false), 3000);
    return () => window.clearInterval(timer);
  }, [hasActiveTask, loadData]);

  const saveConfig = async (values: ContentStudioConfig) => {
    setSaving(true);
    try {
      const saved = await put<ContentStudioConfig>(API_ENDPOINTS.contentStudio.config, values);
      form.setFieldsValue(saved);
      message.success('鹿鸣数据上报配置已保存');
      await loadData(false);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '保存配置失败');
    } finally {
      setSaving(false);
    }
  };

  const startReporting = async () => {
    if (!actions.length) {
      message.warning('请至少选择一类上报数据');
      return;
    }
    setReporting(true);
    try {
      await post(API_ENDPOINTS.lumingReporting.tasks, { actions });
      message.success('鹿鸣数据上报任务已创建');
      await loadData(false);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '创建上报任务失败');
    } finally {
      setReporting(false);
    }
  };

  const taskColumns: ColumnsType<ReportTask> = [
    { title: '任务', dataIndex: 'id', width: 90, render: (value) => `#${value}` },
    {
      title: '范围', width: 110,
      render: (_, item) => (item.scope === 'all' ? '全部绑定用户' : `用户 ${item.user_id}`),
    },
    {
      title: '内容', dataIndex: 'actions', width: 280,
      render: (values: ReportAction[]) => values.map((value) => (
        <Tag key={value}>{actionOptions.find((item) => item.value === value)?.label || value}</Tag>
      )),
    },
    {
      title: '状态', dataIndex: 'status', width: 110,
      render: (value: ReportTask['status']) => <Tag color={taskStatus[value]?.color}>{taskStatus[value]?.label || value}</Tag>,
    },
    { title: '结果', dataIndex: 'message', ellipsis: true },
    { title: '创建时间', dataIndex: 'created_at', width: 180, render: dateText },
  ];

  const recordColumns: ColumnsType<ReportRecord> = [
    { title: '记录', dataIndex: 'id', width: 90, render: (value) => `#${value}` },
    { title: '类型', dataIndex: 'action_label', width: 120 },
    {
      title: '状态', dataIndex: 'status', width: 90,
      render: (value: ReportRecord['status']) => <Tag color={reportStatus[value]?.color}>{reportStatus[value]?.label || value}</Tag>,
    },
    {
      title: '用户映射', width: 180,
      render: (_, item) => item.account_id ? `本地 ${item.user_id} → 鹿鸣 ${item.account_id}` : `本地 ${item.user_id || '-'}`,
    },
    { title: '业务对象', dataIndex: 'entity_id', width: 190, ellipsis: true, render: (value) => value || '-' },
    { title: '结果', dataIndex: 'message', ellipsis: true },
    { title: 'HTTP', dataIndex: 'http_status', width: 80, render: (value) => value || '-' },
    { title: '时间', dataIndex: 'created_at', width: 180, render: dateText },
    { title: '操作', width: 80, fixed: 'right', render: (_, item) => <Button type="link" onClick={() => setDetail(item)}>详情</Button> },
  ];

  const metrics = useMemo(() => ({
    bound: overview?.users.bound || 0,
    unbound: overview?.users.unbound || 0,
    projects: overview?.projects || 0,
    success: overview?.reports.success || 0,
    failed: overview?.reports.failed || 0,
  }), [overview]);

  return (
    <div className="data-reporting-page">
      <div className="reporting-header">
        <div>
          <h1>鹿鸣数据上报</h1>
          <Text type="secondary">将灵笔小说用户、作品、墨水资产、充值订单和钱包流水同步到鹿鸣内容中台</Text>
        </div>
        <Button icon={<ReloadOutlined />} loading={loading} onClick={() => loadData()}>刷新</Button>
      </div>

      <Alert
        showIcon
        type={metrics.unbound ? 'warning' : 'info'}
        message={`已绑定 ${metrics.bound} 位用户，未绑定 ${metrics.unbound} 位用户`}
        description="鹿鸣要求 accountId 必须来自统一登录/SSO。未绑定用户会安全跳过，系统不会使用本地用户 ID 冒充鹿鸣账号。"
      />

      <Row className="reporting-metrics" gutter={20}>
        <Col xs={12} lg={5}><Statistic title="已绑定用户" value={metrics.bound} /></Col>
        <Col xs={12} lg={5}><Statistic title="待上报作品" value={metrics.projects} /></Col>
        <Col xs={12} lg={5}><Statistic title="累计成功" value={metrics.success} /></Col>
        <Col xs={12} lg={5}><Statistic title="累计失败" value={metrics.failed} valueStyle={metrics.failed ? { color: '#cf1322' } : undefined} /></Col>
        <Col xs={24} lg={4}><Statistic title="直连充值订单" value={overview?.orders || 0} /></Col>
      </Row>

      <Card
        className="reporting-config"
        title="鹿鸣接口配置"
        extra={<Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => form.submit()}>保存配置</Button>}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={saveConfig}
          initialValues={{ enabled: true, platform_code: 'ai_novel', base_url: 'https://content-studio-h5.cestalt.com' }}
        >
          <Row gutter={18}>
            <Col xs={24} md={4}>
              <Form.Item name="enabled" label="启用上报" valuePropName="checked"><Switch checkedChildren="启用" unCheckedChildren="停用" /></Form.Item>
            </Col>
            <Col xs={24} md={7}>
              <Form.Item name="platform_code" label="platformKey" rules={[{ required: true }]}><Input /></Form.Item>
            </Col>
            <Col xs={24} md={13}>
              <Form.Item name="base_url" label="鹿鸣 API Base URL" rules={[{ required: true, type: 'url' }]}><Input /></Form.Item>
            </Col>
          </Row>
          <Form.Item name="sync_secret" label="Secret" rules={[{ required: true, message: '请输入鹿鸣签名 Secret' }]}>
            <Input.Password visibilityToggle autoComplete="new-password" />
          </Form.Item>
          <Form.Item name="recharge_url" hidden><Input /></Form.Item>
          <Form.Item name="last_sync_status" label="最近状态"><Input disabled /></Form.Item>
          <Paragraph type="secondary" className="secret-note">
            Secret 仅保存在服务端配置中，用于 HMAC-SHA256 签名；不会写入前端构建产物、上报日志或请求正文。
          </Paragraph>
        </Form>
      </Card>

      <Card className="reporting-action" title="手动上报">
        <Space direction="vertical" size={16} className="reporting-action-content">
          <Checkbox.Group
            options={actionOptions}
            value={actions}
            onChange={(values) => setActions(values as ReportAction[])}
          />
          <Space wrap>
            <Button
              type="primary"
              size="large"
              icon={<SendOutlined />}
              disabled={hasActiveTask || !overview?.config.enabled}
              loading={reporting}
              onClick={startReporting}
            >
              {hasActiveTask ? '正在上报' : '立即上报全部绑定用户'}
            </Button>
            <Text type="secondary">重复订单和流水使用稳定业务 ID；内容未变化时系统会自动跳过。</Text>
          </Space>
        </Space>
      </Card>

      <Card className="reporting-table-card" title={<Space>上报任务{hasActiveTask && <Tag color="processing">自动刷新中</Tag>}</Space>}>
        <Table rowKey="id" columns={taskColumns} dataSource={tasks} loading={loading} pagination={{ pageSize: 6 }} scroll={{ x: 980 }} />
      </Card>

      <Card className="reporting-table-card" title="上报明细">
        <Table rowKey="id" columns={recordColumns} dataSource={records} loading={loading} pagination={{ pageSize: 12 }} scroll={{ x: 1250 }} />
      </Card>

      <Drawer title={`上报记录 #${detail?.id || ''}`} width={720} open={!!detail} onClose={() => setDetail(undefined)}>
        {detail && (
          <Space direction="vertical" size={18} className="reporting-detail">
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="类型">{detail.action_label}</Descriptions.Item>
              <Descriptions.Item label="状态"><Tag color={reportStatus[detail.status]?.color}>{reportStatus[detail.status]?.label}</Tag></Descriptions.Item>
              <Descriptions.Item label="本地用户">{detail.user_id || '-'}</Descriptions.Item>
              <Descriptions.Item label="鹿鸣 accountId">{detail.account_id || '-'}</Descriptions.Item>
              <Descriptions.Item label="请求方式">{detail.method || '-'}</Descriptions.Item>
              <Descriptions.Item label="HTTP 状态">{detail.http_status || '-'}</Descriptions.Item>
              <Descriptions.Item label="请求 ID" span={2}>{detail.request_id || '-'}</Descriptions.Item>
              <Descriptions.Item label="接口" span={2}>{detail.endpoint || '-'}</Descriptions.Item>
              <Descriptions.Item label="结果" span={2}>{detail.message}</Descriptions.Item>
            </Descriptions>
            <div><Text strong>请求数据</Text><pre>{pretty(detail.payload)}</pre></div>
            <div><Text strong>鹿鸣响应</Text><pre>{pretty(detail.response)}</pre></div>
          </Space>
        )}
      </Drawer>
    </div>
  );
};

export default DataReportingPage;
